import { useEffect, useState } from 'react';

/**
 * Per-second countdown to a target ISO timestamp. Pure presentation —
 * the consumer decides what to do when the deadline passes (typically:
 * stop showing the row). Pauses ticks while the tab is hidden so we
 * don't burn CPU on a background tab, then snaps to the correct value
 * the moment the tab becomes visible again.
 *
 * Two presentation modes share one ticker so the dashboard banner and
 * the global header indicator stay in sync at the millisecond level:
 *   - `variant="full"`   → "2d 14h 03m 21s" (default; banner copy)
 *   - `variant="compact"`→ "2d 14h" / "14h 03m" / "03m 21s"
 *     (auto-truncates to the two most significant units for header)
 *
 * Both variants expose a hidden accessible label so screen readers
 * announce a human phrase ("2 days, 14 hours, 3 minutes, 21 seconds
 * remaining") instead of stuttering through the abbreviated display
 * once a second.
 */
interface CountdownTimerProps {
  target: string;
  variant?: 'full' | 'compact';
  className?: string;
  /**
   * Fires once when the countdown crosses zero. Lets the banner / header
   * trigger a query refetch so an expired rodeo drops out of the list.
   */
  onExpire?: () => void;
}

interface Parts {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  expired: boolean;
}

function diff(targetMs: number, nowMs: number): Parts {
  const ms = Math.max(0, targetMs - nowMs);
  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / 86_400);
  const hours = Math.floor((totalSeconds % 86_400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return { days, hours, minutes, seconds, expired: ms <= 0 };
}

function pad(n: number) {
  return String(n).padStart(2, '0');
}

/**
 * Long-form countdown — `01 days, 16 hrs, 52 mins, 02 secs`. All four
 * units are always shown (with leading zeros) so the row's width never
 * jumps around mid-tick. Used in the prominent dashboard banner.
 */
function formatFull(parts: Parts): string {
  if (parts.expired) return 'Entries closed';
  const { days, hours, minutes, seconds } = parts;
  return `${pad(days)} days, ${pad(hours)} hrs, ${pad(minutes)} mins, ${pad(seconds)} secs`;
}

/**
 * Compact countdown for the header pill — `01d 16h 52m 02s`. Same four
 * units but with single-letter suffixes so multiple rodeos can fit
 * side-by-side in the header bar on desktop. Width is stable
 * tick-to-tick (always 4 padded units), so the surrounding pill
 * doesn't reflow every second.
 */
function formatCompact(parts: Parts): string {
  if (parts.expired) return 'Closed';
  const { days, hours, minutes, seconds } = parts;
  return `${pad(days)}d ${pad(hours)}h ${pad(minutes)}m ${pad(seconds)}s`;
}

function formatAccessible(parts: Parts): string {
  if (parts.expired) return 'Entries are closed';
  const { days, hours, minutes, seconds } = parts;
  const bits: string[] = [];
  if (days) bits.push(`${days} day${days === 1 ? '' : 's'}`);
  if (hours) bits.push(`${hours} hour${hours === 1 ? '' : 's'}`);
  if (minutes) bits.push(`${minutes} minute${minutes === 1 ? '' : 's'}`);
  if (seconds && !days) bits.push(`${seconds} second${seconds === 1 ? '' : 's'}`);
  return `${bits.join(', ')} remaining`;
}

export function CountdownTimer({
  target,
  variant = 'full',
  className,
  onExpire,
}: CountdownTimerProps) {
  const targetMs = new Date(target).getTime();
  const valid = Number.isFinite(targetMs);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!valid) return;
    let intervalId: number | undefined;

    const tick = () => setNow(Date.now());
    const start = () => {
      if (intervalId !== undefined) return;
      intervalId = window.setInterval(tick, 1000);
    };
    const stop = () => {
      if (intervalId !== undefined) {
        window.clearInterval(intervalId);
        intervalId = undefined;
      }
    };

    // Snap immediately on tab visibility return so the user doesn't see
    // a stale value while we wait for the next interval fire.
    const handleVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        tick();
        start();
      }
    };

    if (!document.hidden) start();
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [valid]);

  // Fire onExpire exactly once when the timer crosses zero.
  const expired = valid && now >= targetMs;
  useEffect(() => {
    if (expired) onExpire?.();
  }, [expired, onExpire]);

  if (!valid) {
    return (
      <span className={className} aria-hidden>
        —
      </span>
    );
  }

  const parts = diff(targetMs, now);
  const text = variant === 'compact' ? formatCompact(parts) : formatFull(parts);
  const accessibleLabel = formatAccessible(parts);

  return (
    <span
      className={className}
      role="timer"
      aria-live="off"
      aria-label={accessibleLabel}
    >
      <span aria-hidden>{text}</span>
    </span>
  );
}
